import React from "react";

/**
 * Result panel shown below the action buttons in AgentRunModal — renders
 * status, AI insights, email send result, parsed payload, and raw Oracle
 * response. Pure-presentational.
 */
export default function AgentResultPanel({ result }) {
  if (!result) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm" data-testid="agent-result">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
        Status: {result.status}{result.http_status ? ` · HTTP ${result.http_status}` : ""}
        {result.replayed && <span className="ml-2 text-amber-600">· Replayed from saved query</span>}
      </div>
      {result.message && result.status !== "access_denied" && (
        <div className="text-sm mt-2 text-gray-700">{result.message}</div>
      )}

      {result.status === "access_denied" && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-sm"
          data-testid="agent-access-denied">
          <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-amber-700">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1L0 15h16L8 1zm0 4.5c.4 0 .75.34.7.74l-.3 4.01a.4.4 0 01-.8 0l-.3-4.01c-.05-.4.3-.74.7-.74zM8 13a.9.9 0 110-1.8.9.9 0 010 1.8z"/></svg>
            Outside your data access
          </div>
          <div className="text-sm text-amber-900 mt-1.5">
            This run was blocked before reaching Oracle — one or more values fall outside the
            data access assigned to your account.
          </div>
          {(result.violations || []).length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {result.violations.map((v, i) => (
                <li key={i} className="text-xs bg-white border border-amber-200 rounded-md px-2.5 py-2"
                  data-testid={`access-violation-${i}`}>
                  <div>
                    <span className="font-mono font-bold text-amber-900">{v.value}</span>
                    <span className="text-amber-800"> is not in your allowed {String(v.category || "").replace(/_/g, " ")}s</span>
                    <span className="text-amber-600 font-mono text-[10px] ml-1.5">({v.field})</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Allowed:</span>
                    {(v.allowed || []).map((a) => (
                      <span key={a} className="text-[11px] font-mono bg-amber-100 border border-amber-200 text-amber-900 px-1.5 py-0.5 rounded">{a}</span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 text-[11px] text-amber-700">
            Need wider access? Ask your administrator to update your Data Access rows in Users &amp; Access.
          </div>
        </div>
      )}

      {result.insights && result.insights.headline && (
        <div className="mt-3 rounded-lg border-l-4 border-[#0020C2] bg-white p-3 shadow-sm" data-testid="agent-insights">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-1">AI Insights</div>
          <div className="text-sm font-semibold text-gray-900 mb-2">{result.insights.headline}</div>
          {result.insights.bullets?.length > 0 && (
            <ul className="text-xs text-gray-700 space-y-1 list-disc pl-5 mb-2">
              {result.insights.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
          {result.insights.top_items?.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-1">Top items</div>
              <ul className="text-xs space-y-1">
                {result.insights.top_items.map((it, i) => (
                  <li key={i} className="flex justify-between gap-3 font-mono">
                    <span className="truncate text-gray-800">{it.label}</span>
                    <span className="text-gray-600 shrink-0">{it.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {result.insights && result.insights.skipped && (
        <div className="mt-3 text-[11px] text-gray-500" data-testid="agent-insights-skipped">
          AI insights skipped: {result.insights.skipped}
        </div>
      )}

      {result.email_result && (
        <div data-testid="agent-email-result"
          className={`mt-3 text-xs px-3 py-2 border rounded-md ${
            result.email_result.sent
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-yellow-200 bg-yellow-50 text-yellow-800"
          }`}>
          <span className="font-bold uppercase tracking-wider text-[10px] mr-2">
            Email {result.email_result.sent ? "Sent" : result.email_result.status}
          </span>
          {result.email_result.recipient ? <span className="font-mono">{result.email_result.recipient}</span> : null}
          {result.email_result.message ? <span> · {result.email_result.message}</span> : null}
        </div>
      )}

      {result.parsed_payload && (
        <>
          <div className="mt-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">LLM-parsed payload</div>
          <pre className="text-[11px] font-mono bg-white border border-gray-200 p-2 mt-1 rounded-md overflow-x-auto max-h-40">{JSON.stringify(result.parsed_payload, null, 2)}</pre>
        </>
      )}

      {result.oracle_response && (
        <>
          <div className="mt-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion response</div>
          <pre className="text-[11px] font-mono bg-white border border-gray-200 p-2 mt-1 rounded-md overflow-x-auto max-h-60">{JSON.stringify(result.oracle_response, null, 2).slice(0, 2000)}</pre>
        </>
      )}
    </div>
  );
}
